import React, { useEffect, useState } from 'react';

interface AdminKeyboardShortcutsProps {
  onRefresh: () => void;
  onViewAllEntries: () => void;
  onExport: () => void;
  onLogout: () => void;
}

const AdminKeyboardShortcuts: React.FC<AdminKeyboardShortcutsProps> = ({
  onRefresh,
  onViewAllEntries,
  onExport,
  onLogout
}) => {
  const [showHelp, setShowHelp] = useState(false);
  const [lastKeyPressed, setLastKeyPressed] = useState('');

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Evitar conflictos con inputs
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      const { key, ctrlKey, metaKey, altKey } = event;
      const isModifierPressed = ctrlKey || metaKey;

      // Atajos de teclado
      if (isModifierPressed) {
        switch (key.toLowerCase()) {
          case 'r':
            event.preventDefault();
            onRefresh();
            setLastKeyPressed('Refresh (Ctrl+R)');
            break;
          case 'e':
            event.preventDefault();
            onExport();
            setLastKeyPressed('Export (Ctrl+E)');
            break;
          case 'a':
            event.preventDefault();
            onViewAllEntries();
            setLastKeyPressed('View All (Ctrl+A)');
            break;
          case 'q':
            event.preventDefault();
            onLogout();
            setLastKeyPressed('Logout (Ctrl+Q)');
            break;
        }
      } else {
        switch (key.toLowerCase()) {
          case '?':
            event.preventDefault();
            setShowHelp(!showHelp);
            setLastKeyPressed('Help (?)');
            break;
          case 'escape':
            setShowHelp(false);
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onRefresh, onViewAllEntries, onExport, onLogout, showHelp]);

  // Limpiar el mensaje después de 2 segundos
  useEffect(() => {
    if (lastKeyPressed) {
      const timer = setTimeout(() => setLastKeyPressed(''), 2000);
      return () => clearTimeout(timer);
    }
  }, [lastKeyPressed]);

  const shortcuts = [
    { key: 'Ctrl + R', description: 'Refresh dashboard data' },
    { key: 'Ctrl + E', description: 'Export CSV' },
    { key: 'Ctrl + A', description: 'View all entries' },
    { key: 'Ctrl + Q', description: 'Logout' },
    { key: '?', description: 'Show/hide this help' },
    { key: 'Esc', description: 'Close help panel' }
  ];

  return (
    <>
      {/* Indicador de tecla presionada */}
      {lastKeyPressed && (
        <div className="fixed bottom-4 left-4 z-50 bg-gray-900 text-white px-4 py-2 rounded-lg shadow-lg animate-slide-in-right">
          <div className="flex items-center space-x-2">
            <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span className="text-sm font-medium">{lastKeyPressed}</span>
          </div>
        </div>
      )}

      {/* Panel de ayuda */}
      {showHelp && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={() => setShowHelp(false)}></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <svg className="w-5 h-5 text-primary-green mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M9.243 3.03a1 1 0 01.727 1.213L9.53 6h2.94l.56-2.243a1 1 0 111.94.486L14.53 6H17a1 1 0 110 2h-2.97l-1 4H15a1 1 0 110 2h-2.47l-.56 2.242a1 1 0 11-1.94-.485L10.47 14H7.53l-.56 2.242a1 1 0 11-1.94-.485L5.47 14H3a1 1 0 110-2h2.97l1-4H5a1 1 0 110-2h2.47l.56-2.243a1 1 0 011.213-.727zM9.03 8l-1 4h2.94l1-4H9.03z" clipRule="evenodd" />
                  </svg>
                  Keyboard Shortcuts
                </h3>
                <button
                  onClick={() => setShowHelp(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>

              <div className="space-y-3">
                {shortcuts.map((shortcut, index) => (
                  <div key={index} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">{shortcut.description}</span>
                    <kbd className="px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-200 border border-gray-300 rounded-lg">
                      {shortcut.key}
                    </kbd>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex justify-center">
                <button
                  onClick={() => setShowHelp(false)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-green hover:bg-primary-green-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-green-light transition-colors"
                >
                  Got it!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Botón de ayuda flotante */}
      <button
        onClick={() => setShowHelp(true)}
        className="fixed bottom-4 right-4 z-40 bg-primary-green text-white p-3 rounded-full shadow-lg hover:bg-primary-green-dark transition-all duration-300 transform hover:scale-110"
        title="Keyboard shortcuts (?)"
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
        </svg>
      </button>
    </>
  );
};

export default AdminKeyboardShortcuts;